import moment from 'moment';

import {
  MONTHLY_PAYMENT_PLAN_GOOGLE_CHAT_SPACE_ID,
  MONTHLY_PAYMENT_PLAN_GOOGLE_CHAT_WEBHOOK_URL,
} from '../constants/lpBillingGoogleChatWebhooks';

export type TLongTermBillingInterval = 'half_year' | 'year';

export type TLongTermPlanEnrollNotification = {
  studentName: string;
  studentEmail: string;
  userId: string;
  country?: string;
  courseId: string;
  courseTitle: string;
  tutorName?: string;
  lessonsPerMonth: number;
  billingInterval: TLongTermBillingInterval;
  currency: string;
  perLessonRate: number;
  totalAmount: number;
  resourceFeeIncluded: boolean;
};

const BILLING_CYCLE_MONTHS: Record<TLongTermBillingInterval, number> = {
  half_year: 6,
  year: 12,
};

const BILLING_LABELS: Record<TLongTermBillingInterval, string> = {
  half_year: '6 months',
  year: '12 months',
};

const getWebhookUrl = (): string =>
  process.env.REACT_APP_MONTHLY_PAYMENT_PLAN_GOOGLE_CHAT_WEBHOOK?.trim() ||
  MONTHLY_PAYMENT_PLAN_GOOGLE_CHAT_WEBHOOK_URL;

const buildGoogleChatMessage = (payload: TLongTermPlanEnrollNotification) => {
  const months = BILLING_CYCLE_MONTHS[payload.billingInterval];
  const billingLabel = BILLING_LABELS[payload.billingInterval];

  return {
    text:
      `📋 *Long-term plan enrolment started*\n\n` +
      `A student clicked *Enrol Now* on a ${billingLabel} monthly learning plan.\n\n` +
      `👤 Student: ${payload.studentName}\n` +
      `📧 Email: ${payload.studentEmail || 'N/A'}\n` +
      `🆔 User ID: ${payload.userId}\n` +
      `🌍 Country: ${payload.country || 'N/A'}\n` +
      `📚 Course: ${payload.courseTitle}\n` +
      `🆔 Course ID: ${payload.courseId}\n` +
      `👨‍🏫 Tutor: ${payload.tutorName || 'N/A'}\n` +
      `📅 Plan: ${
        payload.lessonsPerMonth
      } lessons/month · billed every ${billingLabel} (${
        payload.lessonsPerMonth * months
      } lessons)\n` +
      `💰 Total: ${payload.currency.toUpperCase()} ${payload.totalAmount.toFixed(
        2
      )} (${payload.currency.toUpperCase()} ${payload.perLessonRate.toFixed(
        2
      )}/lesson)\n` +
      `📦 Resources add-on: ${payload.resourceFeeIncluded ? 'Yes' : 'No'}\n` +
      `🕐 At: ${moment().format('YYYY-MM-DD HH:mm:ss')}\n` +
      `💬 Google Chat space: Monthly Payment Plan (${MONTHLY_PAYMENT_PLAN_GOOGLE_CHAT_SPACE_ID})`,
  };
};

/**
 * Notifies the Monthly Payment Plan Google Chat space when a student starts
 * checkout on a 6- or 12-month plan from TopUpCourseModal.
 */
export const sendLongTermPlanEnrollNotification = async (
  payload: TLongTermPlanEnrollNotification
): Promise<{ ok: boolean; errorMessage?: string }> => {
  const webhookUrl = getWebhookUrl();

  try {
    const response = await fetch(webhookUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(buildGoogleChatMessage(payload)),
    });

    if (!response.ok) {
      return {
        ok: false,
        errorMessage: `Google Chat notification failed (HTTP ${response.status}).`,
      };
    }

    return { ok: true };
  } catch (error) {
    const detail = error instanceof Error ? error.message : 'Network error';
    return {
      ok: false,
      errorMessage: `Could not reach Google Chat (${detail}).`,
    };
  }
};
